import React, { useState } from 'react';
import { Alert, Button, Input } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import SparkleSVG from '../assets/media/Sparkle';
import '../assets/css/subscription-sign-up.css';
import { SubscriptionLoading } from '../index';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  usePartnerInviteCodeSignUpMutation,
  useVerifyBrandInviteCodeDetailsQuery,
} from '../registrationSlice';
import { isRTKErrorType } from '../../../store/utils';
import { notification } from '../../../utils/messaging';
import {
  ACCOUNT_CREATION_ROUTE,
  SUBSCRIPTION_SIGN_UP_ROUTE,
} from '../../../app/appConstants';

function BrandPartnerSubscriptionContent(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('code');

  const [formOpen, setFormOpen] = useState<boolean>(false);
  const [email, setEmail] = useState<string | null>(null);

  const {
    data: brandDetails,
    isFetching: validatingCode,
    error: codeValidationError,
  } = useVerifyBrandInviteCodeDetailsQuery(inviteCode, { skip: !inviteCode });
  const [signup, { isLoading: submitting, error: signupError }] =
    usePartnerInviteCodeSignUpMutation();

  const showNoAccountMessage = Boolean(
    signupError && isRTKErrorType(signupError) && signupError?.status === 404
  );

  if (!inviteCode || codeValidationError) {
    if (codeValidationError && isRTKErrorType(codeValidationError)) {
      return (
        <Alert
          message='Something went wrong'
          description={
            codeValidationError?.data?.detail ?? codeValidationError?.data
          }
          type='error'
          showIcon
        />
      );
    }
    return (
      <Alert
        message='Something went wrong'
        description='Invalid link'
        type='error'
        showIcon
      />
    );
  }

  if (validatingCode) {
    return <SubscriptionLoading hideMessage />;
  }

  if (!brandDetails) {
    return (
      <Alert
        message='Something went wrong'
        description='Error fetching brand details'
        type='error'
        showIcon
      />
    );
  }

  return (
    <div className='mx-auto mb-7 mt-5 text-blue-100 '>
      <h2 className='mb-5 font-montserrat text-3xl font-black uppercase text-white'>
        <span className='align-sub leading-[1.5]'>Plus</span>
        <span className='ml-1 mt-[-1em] whitespace-nowrap rounded-full border-[1px] border-white pl-1 pr-1 align-super font-sans text-[0.3em] font-bold uppercase'>
          Pro
        </span>
      </h2>
      <p className='text-3xl font-bold text-white'>
        {brandDetails.brand_name} has provided you with an annual subscription!
      </p>

      <img
        className='mx-auto mb-6 mt-6'
        src={brandDetails?.logo_image ?? brandDetails?.primary_image ?? ''}
        alt='gift'
        width={250}
      />
      <p className='mb-8 text-lg font-medium'>
        Claim your{' '}
        <strong className='font-bold text-white'>12 month subcription</strong>{' '}
        NOW and elevate your swimbooker experience. Enjoy access to:
      </p>
      <ul className='mx-auto mb-8 max-w-fit list-inside list-image-checkmark-green text-left text-base font-bold leading-8 text-white md:max-w-xl md:columns-2'>
        {[
          'Weekly Giveaways',
          'On-The Bank Insurance',
          'Discounts and Cashback',
          'Brand Perks & Unique Products',
          'Advanced Catch Report Data',
          'and much more...',
        ].map((statement, i) => (
          <li key={`reward-${i}`}>
            <span className='relative left-1 top-[-5px]'>{statement}</span>
          </li>
        ))}
      </ul>

      <div className='mb-6 flex w-full flex-row flex-wrap items-center gap-3 sm:flex-nowrap'>
        <Input
          className={`subscription-form h-full rounded-2xl p-[16px] text-lg ${
            formOpen ? 'subscription-form--open' : 'subscription-form--closed'
          }`}
          value={email || undefined}
          onChange={(e) => setEmail(e.target.value)}
          placeholder='Enter your email'
        />
        <div
          className={`bg-bright-gradient subscription-button mx-auto w-full max-w-sm rounded-2xl p-[2px] ${
            formOpen ? 'subscription-button--form-open' : ''
          }`}
        >
          <Button
            onClick={() => {
              if (!formOpen) {
                setFormOpen(true);
                return;
              }
              if (submitting) {
                return;
              }
              if (!email) {
                notification.error({
                  description: 'Please enter your email.',
                });
                return;
              }
              signup({
                email: email,
                invite_code: inviteCode,
              })
                .unwrap()
                .then(() => {
                  navigate(
                    `${SUBSCRIPTION_SIGN_UP_ROUTE}/success?plan=Pro&isInviteSignup=true`
                  );
                })
                .catch(() => {
                  /* Handled by defaultErrorHandler */
                });
            }}
            className='flex h-full w-full flex-row items-center whitespace-normal rounded-[calc(theme(borderRadius.2xl)-2px)] border-none bg-black p-3 text-lg font-normal text-white'
            loading={submitting}
          >
            <div className='flex flex-1 flex-row items-center justify-center'>
              {formOpen ? (
                <span className='mr-4 whitespace-nowrap'>Sign Up</span>
              ) : (
                <>
                  <SparkleSVG className='mr-5 text-3xl' />
                  <span className='leading-[1]'>
                    Join{' '}
                    <strong className='font-montserrat font-black uppercase'>
                      Plus
                    </strong>{' '}
                    Now!
                  </span>
                </>
              )}
              <RightOutlined className='ml-auto' />
            </div>
          </Button>
        </div>
      </div>
      <p className='mx-auto max-w-sm text-sm font-medium'>
        If you have an existing swimbooker account, use the same email when
        signing up!
      </p>
      {showNoAccountMessage && (
        <p className='mx-auto mt-6 max-w-sm text-sm font-medium'>
          If you don&apos;t have a swimbooker account yet, you can{' '}
          <Link to={ACCOUNT_CREATION_ROUTE} className='uppercase underline'>
            sign up here
          </Link>
        </p>
      )}
    </div>
  );
}

export default BrandPartnerSubscriptionContent;
